/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Alert } from "../../../assets/svg/alert.svg";
import { InstrumentTypeResponse } from "../../../codegen-api";
import { MARK_PRICE_COLLAR } from "../../../constants/precision/form";
import { MarketContext } from "../../../contexts/MarketContext";
import { getAssetShortName } from "../../../utils/asset/assets";
import { getAssetFromSymbol } from "../../../utils/instruments";
import { truncateString } from "../../../utils/strings";
import { OrderbookFilterEnum } from "../../Perps/TradeOrderbookSection/shared";
import {
  RowWithSizeIndicator,
  TableHeaderWrapper,
} from "../../shared/Table/style";
import TooltipExplanation from "../../shared/Tooltip";
import {
  AskBar,
  BidAskBarWrapper,
  BidBar,
  CellsWrapper,
  ProtectionBarrierRow,
  TradeCol,
  TradeOrderBookWrapper,
} from "./style";
import { renderAskCells, renderBidCells } from "./cells";
import {
  headers,
  headerFlex,
  IHeaderType,
  ITradeOrderbookProps,
} from "./types";

function TradeOrderbook({
  bids,
  asks,
  sizeDecimals,
  priceDecimals,
  accountOrders,
  instrumentId,
  onRowClick,
  middleRow,
  showIV,
  markPrice,
  indexPrice,
  indexPriceCollar,
  headerRowHeight,
  rowHeight,
  showValueInUSD,
  filter,
  orderbookTicksize,
}: ITradeOrderbookProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TicketDetails.TradeOrderbook",
  });

  // Sort asks and bids ascending, from lowest price to highest price.
  // Make sure theres always a balanced no of rows on both sides
  const { market } = useContext(MarketContext);
  const rawAsset = getAssetFromSymbol(instrumentId || "");
  const asset = getAssetShortName(rawAsset) || rawAsset;

  let sortedBids = bids;
  let sortedAsks = asks;

  // Adds back zero values if available
  if (bids.length > sortedBids.length) {
    const diff = bids.length - sortedBids.length;
    sortedBids = [...sortedBids, ...Array(diff).fill(["0", "0"])];
  }
  if (asks.length > sortedAsks.length) {
    const diff = asks.length - sortedAsks.length;
    sortedAsks = [...sortedAsks, ...Array(diff).fill(["0", "0"])];
  }

  const protectionBarrierContent = useCallback(
    (type: "bid" | "ask") => {
      if (market.derivative === InstrumentTypeResponse.Option) {
        return (
          <TooltipExplanation
            title={t("order_protection_barrier")}
            explanation={
              <>
                {t("order_protection_barrier_explanation_1", {
                  type: type === "bid" ? "bids" : "offers",
                })}
                <br />
                <br />
                {type === "bid" ? (
                  <>
                    {t("order_protection_barrier_explanation_2")}
                    <br />
                    <strong>
                      {t("order_protection_barrier_explanation_3", {
                        markCollar: MARK_PRICE_COLLAR,
                        indexCollar: indexPriceCollar,
                      })}
                    </strong>
                  </>
                ) : (
                  <>
                    {t("order_protection_barrier_explanation_4")}
                    <br />
                    <strong>
                      {t("order_protection_barrier_explanation_5", {
                        markCollar: MARK_PRICE_COLLAR,
                        indexCollar: indexPriceCollar,
                      })}
                    </strong>
                  </>
                )}
              </>
            }
            renderContent={({ ref, ...triggerHandler }) => (
              <ProtectionBarrierRow
                $type={type}
                ref={ref}
                {...triggerHandler}
                onClick={(e) => e.stopPropagation()}
              >
                {t("order_protection_barrier")} <Alert />
              </ProtectionBarrierRow>
            )}
          />
        );
      }

      return undefined;
    },
    [indexPriceCollar, market.derivative, t]
  );

  const getHeaderTitle = useCallback(
    (header: IHeaderType) => {
      switch (header) {
        case "Cumulative":
          return t("cumulative");
        case "Price / IV":
          return t("price_iv");
        case "Size":
          return t("size");
        default:
          return "";
      }
    },
    [t]
  );

  const askCellsContent = renderAskCells({
    filter,
    sortedAsks,
    markPrice,
    indexPrice,
    indexPriceCollar,
    showValueInUSD,
    accountOrders,
    orderbookTicksize,
    instrumentId,
    onRowClick,
    showIV,
    sizeDecimals,
    priceDecimals,
    rowHeight,
    protectionBarrierContent,
  });

  const bidCellsContent = renderBidCells({
    filter,
    sortedBids,
    markPrice,
    indexPrice,
    indexPriceCollar,
    showValueInUSD,
    accountOrders,
    orderbookTicksize,
    instrumentId,
    onRowClick,
    showIV,
    sizeDecimals,
    priceDecimals,
    rowHeight,
    protectionBarrierContent,
  });

  const totalBidSize = sortedBids.reduce(
    (acc, v) =>
      acc + (showValueInUSD ? Number(v[0]) * Number(v[1]) : Number(v[1])),
    0
  );
  const totalAskSize = sortedAsks.reduce(
    (acc, v) =>
      acc + (showValueInUSD ? Number(v[0]) * Number(v[1]) : Number(v[1])),
    0
  );
  const totalCombinedSize = totalBidSize + totalAskSize;
  const bidPercent =
    totalCombinedSize > 0 ? (totalBidSize / totalCombinedSize) * 100 : 50;
  const askPercent = 100 - bidPercent;

  return (
    <TradeOrderBookWrapper>
      <TableHeaderWrapper $height={headerRowHeight}>
        <RowWithSizeIndicator>
          {headers.map((header) => {
            if (header === "Price / IV") {
              return (
                <TradeCol
                  key={header}
                  isHeader
                  style={{ flex: headerFlex[header] }}
                >
                  {showIV ? t("price_iv") : t("price")}
                </TradeCol>
              );
            }
            if (header === "Size" || header === "Cumulative") {
              return (
                <TradeCol
                  key={header}
                  isHeader
                  style={{ flex: headerFlex[header] }}
                >
                  {getHeaderTitle(header)} (
                  {showValueInUSD ? "USD" : truncateString(asset || "", 4)})
                </TradeCol>
              );
            }
            return (
              <TradeCol
                key={header}
                isHeader
                style={{ flex: headerFlex[header] }}
              >
                {getHeaderTitle(header)}
              </TradeCol>
            );
          })}
        </RowWithSizeIndicator>
      </TableHeaderWrapper>
      <CellsWrapper
        style={
          filter
            ? {
                justifyContent:
                  filter === OrderbookFilterEnum.BIDS
                    ? "flex-start"
                    : "flex-end",
              }
            : {
                justifyContent: "center",
              }
        }
      >
        <div>
          {/* ASKS */}
          {askCellsContent}
          {/* MIDDLE ROW */}
          {!!middleRow && middleRow}
          {/* BIDS */}
          {bidCellsContent}
        </div>
      </CellsWrapper>
      {totalCombinedSize > 0 && (
        <BidAskBarWrapper>
          <BidBar style={{ width: `${bidPercent}%` }}>
            Bid {bidPercent.toFixed(2)}%
          </BidBar>
          <AskBar>
            {askPercent.toFixed(2)}% Ask
          </AskBar>
        </BidAskBarWrapper>
      )}
    </TradeOrderBookWrapper>
  );
}

export default TradeOrderbook;
